import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { fetchProductsPage } from "@/lib/api";
import { resolveMediaUrl } from "@/lib/api";
import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import { Skeleton } from "@/components/ui/skeleton";
import { SectionHeading } from "@/components/SectionHeading";
import { Volume2, VolumeX } from "lucide-react";

/**
 * "Trending Now" — a video-only spotlight carousel. Shows only items whose
 * cover media is a video (photos are skipped entirely for this section).
 * Each video autoplays muted; when it finishes, the carousel automatically
 * advances to the next trending video, looping back to the first after the
 * last. Renders nothing if there are no video items yet.
 */
export const TrendingHighlights = () => {
  const [videos, setVideos] = useState(null);
  const [api, setApi] = useState(null);
  const [selected, setSelected] = useState(0);
  const [muted, setMuted] = useState(true);
  const videoRefs = useRef([]);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Pull a generous batch across all categories, then keep only videos.
        const data = await fetchProductsPage({ limit: 50 });
        const videoItems = data.filter((i) => i.media_type === "video" && i.media_url);
        if (!cancelled) setVideos(videoItems);
      } catch {
        if (!cancelled) setVideos([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!api) return;
    const onSelect = () => {
      const i = api.selectedScrollSnap();
      setSelected(i);
      // Restart playback on whichever video just became active.
      videoRefs.current.forEach((v, idx) => {
        if (!v) return;
        if (idx === i) {
          v.currentTime = 0;
          v.muted = muted;
          v.play().catch(() => {});
        } else {
          v.pause();
        }
      });
    };
    api.on("select", onSelect);
    onSelect();
    return () => api.off("select", onSelect);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  const handleVideoEnded = useCallback(() => {
    api?.scrollNext();
  }, [api]);

  const toggleMute = () => {
    const v = videoRefs.current[selected];
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  if (videos && videos.length === 0) return null;

  return (
    <section className="relative py-14 sm:py-20 bg-background-alt" data-testid="trending-highlights">
      <div className="max-w-sm sm:max-w-md mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Handpicked"
          title="Trending Now"
          subtitle="Jewellery videos everyone's watching right now."
        />

        {!videos ? (
          <Skeleton className="aspect-[9/16] max-h-[70vh] mx-auto rounded-2xl" />
        ) : (
          <div className="relative">
            <Carousel setApi={setApi} opts={{ loop: true }}>
              <CarouselContent className="ml-0">
                {videos.map((item, i) => (
                  <CarouselItem key={item.id} className="pl-0">
                    <div
                      className="group relative aspect-[9/16] rounded-2xl overflow-hidden bg-black cursor-pointer"
                      onClick={() => navigate(`/all-items?category=${item.category}`)}
                      data-testid={`trending-video-${item.id}`}
                    >
                      <video
                        ref={(el) => (videoRefs.current[i] = el)}
                        src={resolveMediaUrl(item.media_url)}
                        className="absolute inset-0 w-full h-full object-cover"
                        autoPlay={i === 0}
                        muted
                        playsInline
                        onEnded={handleVideoEnded}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent pointer-events-none" />
                      <span className="absolute bottom-4 left-5 font-heading text-lg sm:text-xl font-semibold text-white pointer-events-none">
                        {item.name}
                      </span>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>

            {videos.length > 0 && (
              <button
                type="button"
                onClick={toggleMute}
                className="absolute bottom-4 right-4 h-10 w-10 rounded-full bg-background/85 backdrop-blur flex items-center justify-center border border-primary/30 hover:bg-background transition-colors z-10"
                aria-label={muted ? "Unmute video" : "Mute video"}
                data-testid="trending-mute-btn"
              >
                {muted ? <VolumeX className="h-4 w-4 text-foreground" /> : <Volume2 className="h-4 w-4 text-primary" />}
              </button>
            )}

            {videos.length > 1 && (
              <div className="flex justify-center gap-2 mt-5" data-testid="trending-dots">
                {videos.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => api?.scrollTo(i)}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      selected === i ? "w-6 bg-primary" : "w-1.5 bg-border"
                    }`}
                    aria-label={`Go to video ${i + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};
